// UI strings for Asetukset as an index of sub-routes (routes/Settings.tsx, settings/pages.ts;
// docs/design/SIVUPALSTA.md "Asetukset"): the pages, the one-line summary of each row, the
// margin's "who set each shared value and when", and the help lines. Finnish (default) and English
// side by side; en has exactly the same shape.
import { defineArea } from '../area';

export type SettingsPageKey = 'appearance' | 'audio' | 'practice' | 'notifications' | 'account' | 'admin';

export const setPagesFi = {
  pages: {
    appearance: 'Ulkoasu ja kieli',
    audio: 'Ääni',
    practice: 'Harjoittelu',
    notifications: 'Ilmoitukset',
    account: 'Tili',
    admin: 'Ylläpito',
  } as Record<SettingsPageKey, string>,
  /** The index row's second line: what is set now. */
  summary: {
    volume: (pct: number) => (pct === 0 ? 'Mykistetty' : `Äänenvoimakkuus ${pct}\u00a0%`),
    pace: (n: number) => `${n} ${n === 1 ? 'uusi sana' : 'uutta sanaa'} päivässä`,
    reminderAt: (time: string) => `Muistutus noin klo ${time}`,
    reminderNone: 'Ei muistutusta',
    signedIn: (name: string) => `Kirjautuneena: ${name}`,
    admin: 'Yhteiset asetukset, tarkistusjono ja tekniset tiedot',
  },
  /** PC margin: who set each shared value, and when. */
  margin: {
    title: 'Yhteiset asetukset',
    pace: 'Yhteinen tahti',
    reminderTime: 'Muistutusaika',
    setBy: (name: string, date: string) => `${name} asetti ${date}`,
    setByYou: (date: string) => `Asetit ${date}`,
    never: 'Ei vielä muutettu – oletusarvo',
    offline: 'Kuka ja milloin näkyy, kun yhteys palaa.',
  },
  /** The admin's own pointer where a member reads a shared value: it is changed in Ylläpito. */
  changeInAdmin: 'Muuta Ylläpidossa',
  /** The value in force today (the admin's pointer row). */
  now: 'Nyt',
  /** Help: one line under a control; the rest behind this. */
  more: 'Esimerkit',
  help: {
    cursor: 'Koskee vain tätä laitetta.',
    macrons: 'Miten pituusmerkkien puuttuminen lasketaan, kun vastaat valyriaksi.',
    pace: 'Uusien sanojen ja oppituntien tahti teille molemmille.',
  },
};

export const setPagesEn: typeof setPagesFi = {
  pages: {
    appearance: 'Appearance and language',
    audio: 'Sound',
    practice: 'Practice',
    notifications: 'Notifications',
    account: 'Account',
    admin: 'Administration',
  },
  summary: {
    volume: (pct) => (pct === 0 ? 'Muted' : `Volume ${pct} %`),
    pace: (n) => `${n} new ${n === 1 ? 'word' : 'words'} a day`,
    reminderAt: (time) => `Reminder at about ${time}`,
    reminderNone: 'No reminder',
    signedIn: (name) => `Signed in as ${name}`,
    admin: 'Shared settings, review queue and technical details',
  },
  margin: {
    title: 'Shared settings',
    pace: 'Shared pace',
    reminderTime: 'Reminder time',
    setBy: (name, date) => `${name} set it on ${date}`,
    setByYou: (date) => `You set it on ${date}`,
    never: 'Not changed yet – the default',
    offline: 'Who and when shows once you are online.',
  },
  changeInAdmin: 'Change in Administration',
  now: 'Now',
  more: 'Examples',
  help: {
    cursor: 'Applies to this device only.',
    macrons: 'How a missing long-vowel mark counts when you answer in High Valyrian.',
    pace: 'The pace of new words and lessons for both of you.',
  },
};

// Messages.setPages (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('setPages', setPagesFi, setPagesEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof setPagesFi> {}
}
